import { observer } from "mobx-react";
import React, { useContext } from "react";
import {
  Paper,
  Stack,
  Title,
  Text,
  Group,
  Loader,
  Alert,
  Button,
  SimpleGrid,
  Table,
  Badge,
  Anchor,
  Box,
} from "@mantine/core";
import { IconAlertTriangle, IconClock, IconLogin } from "@tabler/icons-react";
import { CheckBitcoinAddressStoreContext } from "../stores/CheckBitcoinAddressStore";
import {
  AddressLookupResult,
  formatBtc,
  formatSats,
  formatUsd,
} from "../helpers/addressLookup";
import {
  AddressQrRow,
  FundsMovedPanel,
  PrivacyNotes,
  UtxoPanel,
  formatDate,
  panelStyle,
} from "./AddressLookupExtras";

const SOURCE_NAMES: Record<string, string> = {
  blockstream: "Blockstream",
  mempool: "mempool.space",
};

const Stat: React.FC<{ label: string; sats: number; price: number | null }> = ({
  label,
  sats,
  price,
}) => (
  <Box>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm" c="white" style={{ fontVariantNumeric: "tabular-nums" }}>
      {formatBtc(sats)}
    </Text>
    {formatUsd(sats, price) && (
      <Text size="xs" c="dimmed">
        {formatUsd(sats, price)} now
      </Text>
    )}
  </Box>
);

const Result: React.FC<{ result: AddressLookupResult }> = ({ result }) => {
  const price = result.btc_usd_price;
  const { confirmed, unconfirmed } = result;
  const pendingNet = unconfirmed.incoming_sats - unconfirmed.outgoing_sats;
  const totalTxs = confirmed.tx_count + unconfirmed.tx_count;
  const firstPending = result.txs.find((tx) => !tx.confirmed);

  return (
    <Stack gap="md">
      <AddressQrRow address={result.address} />

      <Paper p="lg" radius="md" style={panelStyle}>
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            Confirmed balance
          </Text>
          <Title
            order={2}
            c="white"
            style={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
          >
            {formatBtc(confirmed.balance_sats)}
          </Title>
          <Text size="sm" c="gray.4" style={{ fontVariantNumeric: "tabular-nums" }}>
            {formatSats(confirmed.balance_sats)}
            {formatUsd(confirmed.balance_sats, price) &&
              ` · ${formatUsd(confirmed.balance_sats, price)}`}
          </Text>

          {unconfirmed.tx_count > 0 && (
            <Alert
              color="yellow"
              variant="light"
              icon={<IconClock size={18} />}
              mt="xs"
              styles={{ root: { backgroundColor: "rgba(250, 176, 5, 0.08)" } }}
            >
              <Text size="sm">
                {unconfirmed.tx_count} unconfirmed transaction
                {unconfirmed.tx_count === 1 ? "" : "s"}:{" "}
                {unconfirmed.incoming_sats > 0 &&
                  `+${formatBtc(unconfirmed.incoming_sats)} incoming`}
                {unconfirmed.incoming_sats > 0 &&
                  unconfirmed.outgoing_sats > 0 &&
                  ", "}
                {unconfirmed.outgoing_sats > 0 &&
                  `−${formatBtc(unconfirmed.outgoing_sats)} outgoing`}
                . Balance once confirmed:{" "}
                {formatBtc(confirmed.balance_sats + pendingNet)}.{" "}
                <Anchor
                  href={
                    firstPending
                      ? `/unconfirmed-bitcoin-transaction?txid=${firstPending.txid}`
                      : "/unconfirmed-bitcoin-transaction"
                  }
                  c="yellow.4"
                  size="sm"
                >
                  See how soon it confirms →
                </Anchor>
              </Text>
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="md" mt="sm">
            <Stat label="Total received" sats={confirmed.received_sats} price={price} />
            <Stat label="Total sent" sats={confirmed.sent_sats} price={price} />
            <Box>
              <Text size="xs" c="dimmed">
                Transactions
              </Text>
              <Text size="sm" c="white">
                {totalTxs.toLocaleString("en-US")}
              </Text>
            </Box>
          </SimpleGrid>
        </Stack>
      </Paper>

      <FundsMovedPanel result={result} />

      {result.txs.length > 0 && (
        <Paper p="lg" radius="md" style={panelStyle}>
          <Title order={5} c="white" mb="sm">
            {result.more_txs
              ? `Latest ${result.txs.length} of ${totalTxs.toLocaleString("en-US")} transactions`
              : "Transactions"}
          </Title>
          <Table.ScrollContainer minWidth={520}>
            <Table
              verticalSpacing="xs"
              style={{ background: "transparent", fontVariantNumeric: "tabular-nums" }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Text size="xs" c="dimmed">Date</Text>
                  </Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>
                    <Text size="xs" c="dimmed">Amount</Text>
                  </Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>
                    <Text size="xs" c="dimmed">Fee rate</Text>
                  </Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>
                    <Text size="xs" c="dimmed">Status</Text>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.txs.map((tx) => (
                  <Table.Tr key={tx.txid}>
                    <Table.Td>
                      <Anchor
                        href={`https://mempool.space/tx/${tx.txid}`}
                        target="_blank"
                        rel="noopener nofollow"
                        size="xs"
                        c="gray.4"
                        title={tx.txid}
                      >
                        {tx.confirmed ? formatDate(tx.block_time) : "In mempool"}
                      </Anchor>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text size="sm" c={tx.delta_sats >= 0 ? "green.4" : "red.4"}>
                        {tx.delta_sats >= 0 ? "+" : "−"}
                        {formatBtc(Math.abs(tx.delta_sats))}
                      </Text>
                      {price && (
                        <Text size="xs" c="dimmed">
                          {tx.btc_usd_then
                            ? `${formatUsd(Math.abs(tx.delta_sats), tx.btc_usd_then)} then`
                            : "— then"}
                          {` · ${formatUsd(Math.abs(tx.delta_sats), price)} now`}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text size="xs" c="gray.4">
                        {tx.fee_rate != null ? `${tx.fee_rate} sat/vB` : "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      {tx.confirmed ? (
                        <Text size="xs" c="gray.4">
                          {tx.confirmations.toLocaleString("en-US")} conf.
                        </Text>
                      ) : (
                        <Anchor
                          href={`/unconfirmed-bitcoin-transaction?txid=${tx.txid}`}
                          title="Check this transaction: how soon it confirms and how to speed it up"
                        >
                          <Badge color="yellow" variant="light" size="sm" style={{ cursor: "pointer" }}>
                            Unconfirmed →
                          </Badge>
                        </Anchor>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      )}

      {result.utxos && (
        <UtxoPanel utxos={result.utxos} fees={result.fees} price={price} />
      )}

      <PrivacyNotes result={result} />

      <Text size="xs" c="dimmed" ta="center">
        Data from {SOURCE_NAMES[result.source] || result.source}, block{" "}
        {result.tip_height.toLocaleString("en-US")}, checked{" "}
        {new Date(result.fetched_at).toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
        })}
        . "Now" values use today's price; "then" uses the price when the transaction
        confirmed (from July 2025 on).
      </Text>
    </Stack>
  );
};

/** Balance and latest transactions under the address check (mainnet addresses only). */
const AddressLookupPanel: React.FC = observer(() => {
  const store = useContext(CheckBitcoinAddressStoreContext);

  if (store.isLookingUp) {
    return (
      <Paper p="lg" radius="md" style={panelStyle}>
        <Group gap="sm" justify="center">
          <Loader size="sm" color="orange" />
          <Text size="sm" c="dimmed">
            Looking up balance and transactions…
          </Text>
        </Group>
      </Paper>
    );
  }

  if (store.lookupError) {
    return (
      <Alert
        color={store.lookupNeedsLogin ? "orange" : "red"}
        variant="light"
        icon={
          store.lookupNeedsLogin ? <IconLogin size={18} /> : <IconAlertTriangle size={18} />
        }
        styles={{ root: { backgroundColor: "rgba(0, 0, 0, 0.4)" } }}
      >
        <Text size="sm">{store.lookupError}</Text>
        {store.lookupNeedsLogin && (
          <Group gap="sm" mt="sm">
            <Button component="a" href="/login" color="orange" size="xs">
              Log in
            </Button>
            <Button component="a" href="/create-account" variant="outline" color="orange" size="xs">
              Create a free account
            </Button>
          </Group>
        )}
      </Alert>
    );
  }

  if (!store.lookup) return null;
  return <Result result={store.lookup.data} />;
});

export default AddressLookupPanel;
